import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAdminPayment, getAdminPayments } from '../../api';
import {
  PageHeader, StatusBadge, Table, Pagination, EmptyState, TableSkeleton,
  UserCell, SearchInput, AstrologerCell,
} from '../../components/ui';
import Modal from '../../components/ui/Modal';
import OverviewBar from '../../components/ui/OverviewBar';
import ExportButton from '../../components/ui/ExportButton';
import { formatCurrency, formatDateTime } from '../../utils/format';
import { CreditCard, Eye, Filter, X } from 'lucide-react';

const LIMIT = 20;

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-2 border-b border-gray-50 last:border-0">
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="col-span-2 text-xs text-gray-900 break-all">{children}</dd>
    </div>
  );
}

export default function PaymentsPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [purpose, setPurpose] = useState('');
  const [provider, setProvider] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-payments', page, status, purpose, provider, search, dateFrom, dateTo],
    queryFn: () =>
      getAdminPayments({
        page,
        limit: LIMIT,
        status: status || undefined,
        purpose: purpose || undefined,
        provider: provider || undefined,
        search: search || undefined,
        from: dateFrom || undefined,
        to: dateTo || undefined,
      }).then((r) => r.data),
    placeholderData: (prev) => prev,
  });

  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ['admin-payment', detailId],
    queryFn: () => getAdminPayment(detailId!).then((r) => r.data.data),
    enabled: !!detailId,
  });

  const rows = data?.data || [];
  const pagination = data?.pagination;
  const meta = data?.meta || {};
  const payment = detailData?.payment;
  const relatedTx = detailData?.transactions || [];

  const hasFilters = !!(purpose || provider || search || dateFrom || dateTo);
  const clearFilters = () => {
    setPurpose('');
    setProvider('');
    setSearch('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const EXPORT_COLS = [
    { key: '_id', label: 'ID' },
    { key: 'amount', label: 'Amount (paise)' },
    { key: 'purpose', label: 'Purpose' },
    { key: 'provider', label: 'Provider' },
    { key: 'status', label: 'Status' },
    { key: 'providerOrderId', label: 'Provider Order' },
    { key: 'providerPaymentId', label: 'Provider Payment' },
    { key: 'couponCode', label: 'Coupon' },
    { key: 'createdAt', label: 'Date' },
  ];

  return (
    <div className="p-6">
      <PageHeader title="Payments" subtitle="Recharge & order payments with full user tracking">
        <ExportButton data={rows} filename="payments" columns={EXPORT_COLS} />
      </PageHeader>

      <OverviewBar
        items={[
          { label: 'Orders', value: pagination?.total ?? '—', icon: <CreditCard size={14} /> },
          { label: 'Paid total', value: formatCurrency(meta.paidTotal || 0), color: 'text-green-600' },
          { label: 'Paid count', value: meta.paidCount ?? '—' },
          { label: 'Failed', value: meta.failedCount ?? '—', color: 'text-red-600' },
        ]}
        loading={isLoading}
      />

      <div className="card p-4 mb-5">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[200px]">
            <SearchInput
              value={search}
              onChange={(v) => { setSearch(v); setPage(1); }}
              placeholder="Search user, phone, order ID, payment ID…"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {['', 'created', 'attempted', 'paid', 'failed', 'refunded'].map((s) => (
              <button
                key={s}
                onClick={() => { setStatus(s); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg border text-xs capitalize ${status === s ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300'}`}
              >
                {s || 'All'}
              </button>
            ))}
          </div>
          <button onClick={() => setShowFilters(!showFilters)} className={`btn-secondary btn-sm ${showFilters ? 'bg-gray-100' : ''}`}>
            <Filter size={14} />
          </button>
          {hasFilters && (
            <button onClick={clearFilters} className="text-xs text-red-500 hover:underline flex items-center gap-1">
              <X size={12} />Clear
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-gray-100">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Purpose</label>
              <select className="input" value={purpose} onChange={(e) => { setPurpose(e.target.value); setPage(1); }}>
                <option value="">All</option>
                <option value="wallet_recharge">Wallet recharge</option>
                <option value="order_payment">Order payment</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Provider</label>
              <select className="input" value={provider} onChange={(e) => { setProvider(e.target.value); setPage(1); }}>
                <option value="">All</option>
                <option value="razorpay">Razorpay</option>
                <option value="manual">Manual</option>
                <option value="wallet">Wallet</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
              <input type="date" className="input" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
              <input type="date" className="input" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
            </div>
          </div>
        )}
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-5"><TableSkeleton rows={8} cols={8} /></div>
        ) : rows.length === 0 ? (
          <EmptyState message="No payment orders" icon={<CreditCard size={32} />} />
        ) : (
          <Table>
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="table-th">User</th>
                <th className="table-th">Amount</th>
                <th className="table-th">Purpose</th>
                <th className="table-th">Provider</th>
                <th className="table-th">Provider IDs</th>
                <th className="table-th">Coupon</th>
                <th className="table-th">Status</th>
                <th className="table-th">Wallet</th>
                <th className="table-th">Date</th>
                <th className="table-th"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p: any) => (
                <tr key={p._id} className="table-row cursor-pointer" onClick={() => setDetailId(p._id)}>
                  <td className="table-td" onClick={(e) => e.stopPropagation()}>
                    <UserCell entity={p.userId} />
                  </td>
                  <td className="table-td">
                    <div className="font-semibold">{formatCurrency(p.amount)}</div>
                    {p.couponDiscount > 0 ? (
                      <div className="text-[10px] text-green-600">−{formatCurrency(p.couponDiscount)} coupon</div>
                    ) : null}
                  </td>
                  <td className="table-td text-xs capitalize">{p.purpose?.replace(/_/g, ' ')}</td>
                  <td className="table-td text-xs capitalize">{p.provider}</td>
                  <td className="table-td text-[10px] font-mono text-gray-500 max-w-[140px]">
                    <p className="truncate" title={p.providerOrderId}>{p.providerOrderId || '—'}</p>
                    <p className="truncate" title={p.providerPaymentId}>{p.providerPaymentId || ''}</p>
                  </td>
                  <td className="table-td text-xs">{p.couponCode || '—'}</td>
                  <td className="table-td"><StatusBadge status={p.status} /></td>
                  <td className="table-td text-xs">
                    {p.walletCredited ? (
                      <span className="text-green-600 font-medium">Credited</span>
                    ) : (
                      <span className="text-gray-400">Pending</span>
                    )}
                  </td>
                  <td className="table-td text-xs whitespace-nowrap">{formatDateTime(p.createdAt)}</td>
                  <td className="table-td">
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={(e) => { e.stopPropagation(); setDetailId(p._id); }}
                    >
                      <Eye size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {pagination?.pages > 1 && (
          <div className="p-4 border-t">
            <Pagination page={page} pages={pagination.pages} total={pagination.total} limit={LIMIT} onPageChange={setPage} />
          </div>
        )}
      </div>

      <Modal open={!!detailId} onClose={() => setDetailId(null)} title="Payment details" size="lg">
        {detailLoading || !payment ? (
          <div className="py-8 text-center text-sm text-gray-500">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
              <StatusBadge status={payment.status} />
              <span className="badge badge-gray capitalize">{payment.purpose?.replace(/_/g, ' ')}</span>
              <span className="badge badge-gray capitalize">{payment.provider}</span>
              <span className="text-lg font-bold ml-auto">{formatCurrency(payment.amount)}</span>
            </div>

            <dl>
              <DetailRow label="User">
                <UserCell entity={payment.userId} />
              </DetailRow>
              <DetailRow label="Currency">{payment.currency || 'INR'}</DetailRow>
              <DetailRow label="Coupon">{payment.couponCode || '—'} {payment.couponDiscount ? `(−${formatCurrency(payment.couponDiscount)})` : ''}</DetailRow>
              <DetailRow label="Wallet credited">{payment.walletCredited ? 'Yes' : 'No'}</DetailRow>
              <DetailRow label="Provider order"><span className="font-mono">{payment.providerOrderId || '—'}</span></DetailRow>
              <DetailRow label="Provider payment"><span className="font-mono">{payment.providerPaymentId || '—'}</span></DetailRow>
              <DetailRow label="Payment ID"><span className="font-mono">{payment._id}</span></DetailRow>
              <DetailRow label="Created">{formatDateTime(payment.createdAt)}</DetailRow>
              <DetailRow label="Updated">{formatDateTime(payment.updatedAt)}</DetailRow>
            </dl>

            {relatedTx.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2">Related wallet transactions</p>
                <div className="rounded-xl border border-gray-100 divide-y">
                  {relatedTx.map((t: any) => (
                    <div key={t._id} className="px-3 py-2 flex items-center gap-3 text-xs">
                      <span className={`font-semibold ${t.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                        {t.type === 'credit' ? '+' : '-'}{formatCurrency(t.amount)}
                      </span>
                      <span className="capitalize text-gray-600">{t.category?.replace(/_/g, ' ')}</span>
                      <span className="text-gray-400 flex-1 truncate">{t.description}</span>
                      {t.userType === 'astrologer' ? (
                        <AstrologerCell entity={t.party || { _id: t.userId }} />
                      ) : (
                        <UserCell entity={t.party || { _id: t.userId }} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
